import React, { useEffect } from 'react'
import { Controller } from 'react-hook-form'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import TextStyle from '@tiptap/extension-text-style'

import { Box, Paper, Toolbar, IconButton, Typography, FormHelperText, Select, MenuItem } from '@mui/material'

import { FormatBold, FormatItalic, FormatUnderlined, FormatStrikethrough, FormatListBulleted, FormatListNumbered } from '@mui/icons-material'

import { useReadonly } from '../../../reusableUtils/useReadonly'
import { useStrings } from '../../../assets/localization/strings'

interface IRichTextEditorProps {
  value: string
  onChange: (value: string) => void
  onBlur: () => void
  label: string
  placeholder: string
  error: boolean
  helperText: string
  disabled: boolean
  focused?: React.MutableRefObject<boolean>
  minHeight: number
  maxHeight: number
  toolbar: boolean
}

export const RichTextEditor = ({ value, onChange, onBlur, label, placeholder, error, helperText, disabled, focused, minHeight, maxHeight, toolbar }: IRichTextEditorProps) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer'
        }
      }),
      // Configure TextStyle with proper attribute handling
      TextStyle.configure({
        HTMLAttributes: {
          class: 'text-style'
        }
      }).extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            fontSize: {
              default: null,
              parseHTML: element => element.style.fontSize,
              renderHTML: attributes => {
                if (!attributes.fontSize) {
                  return {}
                }
                return {
                  style: `font-size: ${attributes.fontSize}`
                }
              }
            },
            fontFamily: {
              default: null,
              parseHTML: element => element.style.fontFamily,
              renderHTML: attributes => {
                if (!attributes.fontFamily) {
                  return {}
                }
                return {
                  style: `font-family: ${attributes.fontFamily}`
                }
              }
            }
          }
        }
      })
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
    },
    onFocus: () => {
      if (focused) focused.current = true
    },
    onBlur: () => {
      onBlur()
      if (focused) focused.current = false
    },
    editable: !disabled,
    editorProps: {
      attributes: {
        placeholder: placeholder,
        style: `
      min-height: ${minHeight}px;
      max-height: ${maxHeight}px;
      overflow-y: auto;
      padding: 12px;
      outline: none;
      font-weight: 400;
    `
      }
    }
  })

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value)
    }
  }, [value, editor])

  const toggleBold = () => editor?.chain().focus().toggleBold().run()
  const toggleItalic = () => editor?.chain().focus().toggleItalic().run()
  const toggleUnderline = () => editor?.chain().focus().toggleUnderline().run()
  const toggleStrike = () => editor?.chain().focus().toggleStrike().run()
  const toggleBulletList = () => editor?.chain().focus().toggleBulletList().run()
  const toggleOrderedList = () => editor?.chain().focus().toggleOrderedList().run()
  const strings = useStrings()


  // Fixed font size and family functions
  const setFontSize = (size: string) => {
    if (size) {
      editor?.chain().focus().setMark('textStyle', { fontSize: size }).run()
    } else {
      editor?.chain().focus().unsetMark('textStyle').run()
    }
  }

  const setFontFamily = (family: string) => {
    if (family) {
      editor?.chain().focus().setMark('textStyle', { fontFamily: family }).run()
    } else {
      editor?.chain().focus().unsetMark('textStyle').run()
    }
  }

  // Get current font size and family for the dropdowns
  const getCurrentFontSize = () => {
    if (!editor) return ''
    const { fontSize } = editor.getAttributes('textStyle')
    return fontSize || ''
  }

  const getCurrentFontFamily = () => {
    if (!editor) return ''
    const { fontFamily } = editor.getAttributes('textStyle')
    return fontFamily || ''
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography
        variant='body2'
        sx={{
          fontSize: '16px',
          fontWeight: 900,
          color: error ? '#d32f2f' : '#17181B',
          backgroundColor: '#fff',
          px: 0.5,
          mb: 1
        }}>
        {label}
      </Typography>

      <Paper
        variant='outlined'
        sx={{
          border: error ? '1px solid #d32f2f' : '1px solid #c4c4c4',
          borderRadius: '8px',
          '&:hover': {
            borderColor: error ? '#d32f2f' : '#000'
          },
          '&:focus-within': {
            borderColor: error ? '#d32f2f' : 'rgb(171, 149, 255)',
            borderWidth: '2px'
          }
        }}>
        {toolbar && (
          <Toolbar
            sx={{
              minHeight: '48px !important',
              borderBottom: '1px solid #e0e0e0',
              px: 1
            }}>
            <IconButton
              size='small'
              onClick={toggleBold}
              sx={{
                color: editor?.isActive('bold') ? '#1976d2' : '#666',
                backgroundColor: editor?.isActive('bold') ? '#d7ccff' : 'transparent'
              }}
              disabled={disabled}>
              <FormatBold />
            </IconButton>

            <IconButton
              size='small'
              onClick={toggleItalic}
              sx={{
                color: editor?.isActive('italic') ? '#1976d2' : '#666',
                backgroundColor: editor?.isActive('italic') ? '#d7ccff' : 'transparent'
              }}
              disabled={disabled}>
              <FormatItalic />
            </IconButton>

            <IconButton
              size='small'
              onClick={toggleUnderline}
              sx={{
                color: editor?.isActive('underline') ? '#1976d2' : '#666',
                backgroundColor: editor?.isActive('underline') ? '#d7ccff' : 'transparent'
              }}
              disabled={disabled}>
              <FormatUnderlined />
            </IconButton>

            <IconButton
              size='small'
              onClick={toggleStrike}
              sx={{
                color: editor?.isActive('strike') ? '#1976d2' : '#666',
                backgroundColor: editor?.isActive('strike') ? '#d7ccff' : 'transparent'
              }}
              disabled={disabled}>
              <FormatStrikethrough />
            </IconButton>

            <IconButton
              size='small'
              onClick={toggleBulletList}
              sx={{
                color: editor?.isActive('bulletList') ? '#1976d2' : '#666',
                backgroundColor: editor?.isActive('bulletList') ? '#d7ccff' : 'transparent'
              }}
              disabled={disabled}>
              <FormatListBulleted />
            </IconButton>

            <IconButton
              size='small'
              onClick={toggleOrderedList}
              sx={{
                color: editor?.isActive('orderedList') ? '#1976d2' : '#666',
                backgroundColor: editor?.isActive('orderedList') ? '#d7ccff' : 'transparent'
              }}
              disabled={disabled}>
              <FormatListNumbered />
            </IconButton>

            {/* Font Size Dropdown */}
            <Select size='small' value={getCurrentFontSize()} displayEmpty onChange={e => setFontSize(e.target.value as string)} disabled={disabled} sx={{ mx: 1, width: 100 }}>
              <MenuItem value=''>{strings.font_size}</MenuItem>
              <MenuItem value='12px'>12px</MenuItem>
              <MenuItem value='16px'>16px</MenuItem>
              <MenuItem value='20px'>20px</MenuItem>
              <MenuItem value='24px'>24px</MenuItem>
              <MenuItem value='28px'>28px</MenuItem>
              <MenuItem value='32px'>32px</MenuItem>
            </Select>

            {/* Font Family Dropdown */}
            <Select size='small' value={getCurrentFontFamily()} displayEmpty onChange={e => setFontFamily(e.target.value as string)} disabled={disabled} sx={{ mx: 1, width: 140 }}>
              <MenuItem value=''>{strings.font_family}</MenuItem>
              <MenuItem value='Arial, sans-serif'>Arial</MenuItem>
              <MenuItem value='"Times New Roman", serif'>Times New Roman</MenuItem>
              <MenuItem value='"Courier New", monospace'>Courier New</MenuItem>
              <MenuItem value='Georgia, serif'>Georgia</MenuItem>
              <MenuItem value='DMSans, sans-serif'>DMSans</MenuItem>
            </Select>
          </Toolbar>
        )}

        <Box
          sx={{
            opacity: disabled ? 0.6 : 1,
            '& .ProseMirror': {
              outline: 'none',
              '& p': {
                margin: 0
              },
              // Allow inline styles to work properly without CSS overrides
              '& p.is-editor-empty:first-child::before': {
                content: 'attr(data-placeholder)',
                float: 'left',
                color: '#adb5bd',
                pointerEvents: 'none',
                height: 0
              }
            }
          }}>
          <EditorContent editor={editor} />
        </Box>
      </Paper>

      {helperText && (
        <FormHelperText error={error} sx={{ mx: 1.75, mt: 0.5 }}>
          {helperText}
        </FormHelperText>
      )}
    </Box>
  )
}

interface IReactHookFormRichTextFieldProps {
  label: string
  name: string
  placeholder?: string
  showHelperError?: boolean
  focused?: React.MutableRefObject<boolean>
  minHeight?: number
  maxHeight?: number
  toolbar?: boolean
}

const ReactHookFormRichTextField = ({
  label,
  name,
  placeholder = '',
  showHelperError = true,
  focused,
  minHeight = 120,
  maxHeight = 300,
  toolbar = true
}: IReactHookFormRichTextFieldProps) => {
  const isReadonly = useReadonly(name)

  return (
    <Controller
      name={name}
      render={({ field, fieldState: { error } }) => (
        <RichTextEditor
          value={field.value || ''}
          onChange={field.onChange}
          onBlur={field.onBlur}
          label={label}
          placeholder={placeholder}
          error={error !== undefined}
          helperText={(showHelperError && error?.message) ?? ''}
          disabled={isReadonly}
          focused={focused}
          minHeight={minHeight}
          maxHeight={maxHeight}
          toolbar={toolbar}
        />
      )}
    />
  )
}

export default ReactHookFormRichTextField
